import { UserProfileErrorCode } from './errorCodes';

/**
 * Error messages for User Profile Epic
 * Maps error codes to user-friendly messages
 */

export const USER_PROFILE_ERROR_MESSAGES: Record<UserProfileErrorCode, string> =
  {
    // Success messages
    [UserProfileErrorCode.SUCCESS]: 'Password changed successfully',

    // Validation errors
    [UserProfileErrorCode.VALIDATION_ERROR]:
      'Invalid input. Please check your information.',
    [UserProfileErrorCode.USER_NOT_FOUND]: 'User not found',
    [UserProfileErrorCode.CURRENT_PASSWORD_INCORRECT]: 'Niepoprawne hasło',
    [UserProfileErrorCode.PASSWORD_REQUIREMENTS_NOT_MET]:
      'Password does not meet requirements',
    [UserProfileErrorCode.PASSWORD_REUSE_ERROR]:
      'Nie można ponownie użyć ostatnich 5 haseł',

    // Client errors
    [UserProfileErrorCode.UNAUTHORIZED]:
      'You are not authorized to perform this action.',
    [UserProfileErrorCode.FORBIDDEN]: 'Access denied. Please contact support.',
    [UserProfileErrorCode.ACCOUNT_DEACTIVATED]: 'Account is deactivated',

    // Server errors
    [UserProfileErrorCode.SERVER_ERROR]:
      'Oops! Something went wrong. Please try again later.',
    [UserProfileErrorCode.INTERNAL_ERROR]:
      'An internal error occurred. Please try again later.',

    // Network errors
    [UserProfileErrorCode.NETWORK_ERROR]:
      'No connection to server. Please check your internet connection.',
    [UserProfileErrorCode.REQUEST_FAILED]:
      'Request failed. Please check your connection and try again.',

    // Unknown errors
    [UserProfileErrorCode.UNKNOWN_ERROR]:
      'An unexpected error occurred. Please try again.',
  };

/**
 * Get error message by error code
 */
export const getErrorMessage = (errorCode: UserProfileErrorCode): string => {
  return (
    USER_PROFILE_ERROR_MESSAGES[errorCode] ||
    USER_PROFILE_ERROR_MESSAGES[UserProfileErrorCode.UNKNOWN_ERROR]
  );
};

/**
 * Interface for API error response
 */
export interface ApiErrorResponse {
  message?: string;
  error?: string;
  details?: Array<{
    path: string;
    message: string;
    code: string;
  }>;
}

/**
 * Extract error message from API response
 */
export const extractErrorMessage = (
  errorData: unknown,
  defaultCode: UserProfileErrorCode = UserProfileErrorCode.UNKNOWN_ERROR
): string => {
  if (!errorData) {
    return getErrorMessage(defaultCode);
  }

  // Handle string errors
  if (typeof errorData === 'string') {
    return errorData;
  }

  // Handle object errors
  if (typeof errorData === 'object') {
    const apiError = errorData as ApiErrorResponse;

    // Check for account deactivated message
    if (
      apiError.message &&
      apiError.message.toLowerCase().includes('deactivated')
    ) {
      return getErrorMessage(UserProfileErrorCode.ACCOUNT_DEACTIVATED);
    }

    // Check for specific error messages
    if (apiError.message) {
      const messageLower = apiError.message.toLowerCase();

      // Map specific error messages to error codes
      if (messageLower.includes('user not found')) {
        return getErrorMessage(UserProfileErrorCode.USER_NOT_FOUND);
      }
      if (
        messageLower.includes('current password') &&
        messageLower.includes('incorrect')
      ) {
        return getErrorMessage(UserProfileErrorCode.CURRENT_PASSWORD_INCORRECT);
      }
      if (
        messageLower.includes('password') &&
        messageLower.includes('requirements')
      ) {
        return getErrorMessage(
          UserProfileErrorCode.PASSWORD_REQUIREMENTS_NOT_MET
        );
      }
      if (
        messageLower.includes('reuse') ||
        messageLower.includes('last 5 passwords')
      ) {
        return getErrorMessage(UserProfileErrorCode.PASSWORD_REUSE_ERROR);
      }

      return apiError.message;
    }

    // Check for validation error with details
    if (
      apiError.details &&
      Array.isArray(apiError.details) &&
      apiError.details.length > 0
    ) {
      // Return the first validation error message
      return (
        apiError.details[0]?.message ||
        getErrorMessage(UserProfileErrorCode.VALIDATION_ERROR)
      );
    }

    if (apiError.error) {
      return apiError.error;
    }
  }

  return getErrorMessage(defaultCode);
};
